import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { LoadScript } from '@react-google-maps/api';
import { useSettings } from '../contexts/SettingsContext';

// Libraries to load - Places API for address autocomplete
const libraries: ("places")[] = ['places'];

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: null,
});

/**
 * Hook to check if Google Maps is loaded and ready to use
 * @returns Object with isLoaded boolean and any load error
 */
export const useGoogleMapsLoaded = (): GoogleMapsContextType => {
  return useContext(GoogleMapsContext);
};

interface GoogleMapsProviderProps {
  children: ReactNode;
}

/**
 * Provider that conditionally loads Google Maps when an API key is configured.
 * If no API key is set, children are rendered without Google Maps functionality.
 * Must be used inside SettingsProvider to access the API key from settings.
 */
export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children }) => {
  const { settings } = useSettings();
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setLoadError(null);
  }, []);

  const handleError = useCallback((error: Error) => {
    setLoadError(error);
    setIsLoaded(false);
    console.error('Google Maps failed to load:', error);
  }, []);

  // If no API key is configured, just render children without Google Maps
  if (!settings.googleMapsApiKey) {
    return (
      <GoogleMapsContext.Provider value={{ isLoaded: false, loadError: null }}>
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  return (
    <LoadScript
      googleMapsApiKey={settings.googleMapsApiKey}
      libraries={libraries}
      onLoad={handleLoad}
      onError={handleError}
    >
      <GoogleMapsContext.Provider value={{ isLoaded, loadError }}>
        {children}
      </GoogleMapsContext.Provider>
    </LoadScript>
  );
};

export default GoogleMapsProvider;
